import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import loader from '../../assets/img/loader.svg';
import GridContainer from '@/components/Grid/GridContainer';
import GridItem from '@/components/Grid/GridItem';
import Card from '@/components/Card/Card';
import CardHeader from '@/components/Card/CardHeader';
import CardBody from '@/components/Card/CardBody';
import FacilitySelect from '@/components/FacilitySelect';
import { Link } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableFooter,
  Grid,
  Box,
} from '@mui/material';
import {
  getBusinessUnits,
  deleteBusinessUnit,
} from '@/api/clients/business-units/business-units.api';

export default function BusinessUnits() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [businessUnits, setBusinessUnit] = useState([]);
  const [selectedFacilityId, setSelectedFacilityId] = useState('');

  useEffect(() => {
    fetchBusinessUnits();
  }, [selectedFacilityId]);

  const fetchBusinessUnits = async () => {
    setLoading(true);
    setBusinessUnit(await getBusinessUnits(selectedFacilityId || null));
    setLoading(false);
  };

  const handleFacilityChange = (facilityId) => {
    setSelectedFacilityId(facilityId);
  };

  const handleDeleteBusinessUnit = async (id) => {
    const isConfirm = confirm(
      'Are you sure you want to delete this business unit?',
    );
    if (!isConfirm) return;
    await deleteBusinessUnit(id);
    fetchBusinessUnits();
  };

  return (
    <GridContainer>
      <GridItem xs={12} sm={12} md={12}>
        <Card>
          <CardHeader color='primary'>
            <h4>{t('businessUnits.title')}</h4>
          </CardHeader>

          <CardBody>
            <div>
              {loading && (
                <div className='loaders'>
                  <img src={loader} alt='Loading...' />
                </div>
              )}

              <Grid container spacing={2} alignItems='center' sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6} md={4}>
                  <Link to={`/facility/create-business-unit`} className='link'>
                    <Box display='flex' alignItems='center' gap={1}>
                      <AddIcon color='primary' />
                      {t('businessUnits.createButton')}
                    </Box>
                  </Link>
                </Grid>
                <Grid item xs={12} sm={6} md={8}>
                  <Box
                    display='flex'
                    gap={2}
                    justifyContent='flex-end'
                    alignItems='center'>
                    <FacilitySelect
                      value={selectedFacilityId}
                      onChange={handleFacilityChange}
                      placeholderKey='businessUnits.filters.facilitiesPlaceholder'
                      parentKey='business_units'
                      childKey='business_units'
                      permission='read'
                    />
                    <div className='custom-search-box'>
                      <input
                        type='search'
                        className='seacrh-field'
                        placeholder='Search by name, email, and phone'
                      />
                      <span className='search-icon'>
                        <SearchIcon />
                      </span>
                    </div>
                  </Box>
                </Grid>
              </Grid>

              <Table width='100%' id='template'>
                <TableHead>
                  <TableRow>
                    <TableCell align='left'>
                      {t('businessUnits.table.nameColumn')}
                    </TableCell>
                    <TableCell align='left'>{t('common.facility')}</TableCell>
                    <TableCell align='left'>
                      {t('businessUnits.table.emailColumn')}
                    </TableCell>
                    <TableCell align='left'>
                      {t('businessUnits.table.phoneColumn')}
                    </TableCell>
                    <TableCell align='left'>
                      {t('businessUnits.table.identifiersColumn')}
                    </TableCell>
                    <TableCell align='left'>
                      {t('businessUnits.table.actionsColumn')}
                    </TableCell>
                  </TableRow>
                </TableHead>

                {businessUnits.length ? (
                  <TableBody>
                    {businessUnits.map((obj, index) => (
                      <TableRow className='txtalign' key={index}>
                        <TableCell width='16%'>{obj.name}</TableCell>
                        <TableCell width='16%'>{obj.facility_name}</TableCell>
                        <TableCell width='16%'>{obj.email}</TableCell>
                        <TableCell width='16%'>{obj.phone}</TableCell>
                        <TableCell width='16%'>
                          {obj.identifiers.join(',')}
                        </TableCell>
                        <TableCell width='20%'>
                          <Link
                            to={`/facility/edit-business-unit/${obj.id}`}
                            title={t('businessUnits.actions.updateTitle')}>
                            <EditIcon color='primary' />
                          </Link>
                          <a
                            style={{ cursor: 'pointer', marginLeft: 8 }}
                            onClick={() => handleDeleteBusinessUnit(obj.id)}
                            title={t('businessUnits.actions.deleteTitle')}>
                            <DeleteIcon color='error' />
                          </a>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                ) : (
                  <TableBody>
                    <TableRow>
                      <TableCell>{t('businessUnits.table.noData')}</TableCell>
                    </TableRow>
                  </TableBody>
                )}

                <TableFooter>
                  <TableRow align='center'>
                    {/* Pagination logic can be added here if needed */}
                  </TableRow>
                </TableFooter>
              </Table>
            </div>
          </CardBody>
        </Card>
      </GridItem>
    </GridContainer>
  );
}
